import { Swiper } from 'swiper';
import { A11y, Navigation } from 'swiper/modules';
import throttle from 'propel/throttle';

const heroCycleBlocks = document.querySelectorAll('.block-hero-cycle');
const headingSlidesContainer = document.querySelector(
	'.block-hero-cycle__heading-slides'
);
const headingSlides = document.querySelectorAll(
	'.block-hero-cycle__heading-slide'
);

if (heroCycleBlocks.length > 0) {
	heroCycleBlocks.forEach((block) => {
		const pauseButton = block.querySelector(
			'.block-hero-cycle__pause-button'
		);

		if (pauseButton) {
			pauseButton.addEventListener('click', () => {
				const video = block.querySelector('video');
				if (!video) return;

				if (video.paused) {
					video.play();
					pauseButton.classList.remove('is-paused');
					pauseButton.setAttribute(
						'aria-label',
						'Pause background video'
					);
				} else {
					video.pause();
					pauseButton.classList.add('is-paused');
					pauseButton.setAttribute(
						'aria-label',
						'Play background video'
					);
				}
			});
		}
	});
}

const setHeadingSlidesHeight = (slides, container) => {
	container.style.removeProperty('--max-slide-height'); // Reset the max height before recalculating to get accurate measurements.

	const maxHeight = Math.max(
		...Array.from(slides).map((slide) => {
			return slide.getBoundingClientRect().height;
		})
	);

	if (maxHeight > 0) {
		container.style.setProperty(
			'--max-slide-height',
			`${maxHeight * 1.15}px`
		);
	}
};

if (headingSlidesContainer && headingSlides?.length > 0) {
	// Defer the layout-forcing getBoundingClientRect call so it doesn't
	// block the first paint during the initial script execution burst.
	requestAnimationFrame(() => {
		setHeadingSlidesHeight(headingSlides, headingSlidesContainer);
	});

	let slideInterval = null;

	const startSlideInterval = () => {
		clearInterval(slideInterval);
		slideInterval = setInterval(() => {
			const activeSlide = headingSlidesContainer.querySelector('.active');
			const nextSlide =
				activeSlide.nextElementSibling || headingSlides[0];

			activeSlide.classList.replace('active', 'slideOut');
			activeSlide.addEventListener(
				'transitionend',
				(e) => {
					if (e.propertyName !== 'transform') return;
					activeSlide.classList.remove('slideOut');
				},
				{ once: true }
			);

			nextSlide.classList.add('active');
		}, 3000);
	};

	const stopSlideInterval = () => {
		clearInterval(slideInterval);
	};

	headingSlidesContainer.classList.add('is-initialized');

	startSlideInterval();

	document.addEventListener('visibilitychange', () => {
		if (document.hidden) {
			stopSlideInterval();
		} else {
			startSlideInterval();
		}
	});

	window.addEventListener(
		'resize',
		throttle(() => {
			setHeadingSlidesHeight(headingSlides, headingSlidesContainer);
		}, 100)
	);
}

const featuredResourcesSlides = document.querySelector(
	'.block-hero-cycle__featured-resources .swiper'
);

if (featuredResourcesSlides) {
	const initFeaturedResources = (slider) => {
		const slides = slider.querySelectorAll('.swiper-slide');

		const settings = {
			loop: slides.length > 1 ? true : false,
			grabCursor: true,
			spaceBetween: 32,
			slidesPerView: 1,
			modules: [A11y, Navigation],
			a11y: {
				itemRoleDescriptionMessage: 'slide',
			},
			navigation: {
				prevEl: `.block-hero-cycle__featured-resources-navigation .swiper-prev`,
				nextEl: `.block-hero-cycle__featured-resources-navigation .swiper-next`,
			},
		};

		new Swiper(slider, settings);
	};

	const featuredObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				featuredObserver.unobserve(entry.target);
				initFeaturedResources(entry.target);
			});
		},
		{ rootMargin: '200px 0px' }
	);

	featuredObserver.observe(featuredResourcesSlides);
}

const cardsContainer = document.querySelector('.block-hero-cycle-cards');

if (cardsContainer) {
	const cardsContent = cardsContainer.querySelector(
		'.block-hero-cycle-cards__content'
	);
	const cards = cardsContainer.querySelectorAll('.block-hero-cycle-card');
	let cardSlider = null;

	const initializeCardSlider = () => {
		if (window.innerWidth < 768) {
			if (!cardSlider && cards.length > 1) {
				cardsContainer.classList.add('swiper');
				cardsContent.classList.add('swiper-wrapper');

				const settings = {
					loop: true,
					grabCursor: true,
					spaceBetween: 8,
					slidesPerView: 1,
					loopAdditionalSlides: 1,
					modules: [A11y, Navigation],
					a11y: {
						itemRoleDescriptionMessage: 'slide',
					},
					navigation: {
						prevEl: cardsContainer.querySelector(
							'.swiper-button-prev'
						),
						nextEl: cardsContainer.querySelector(
							'.swiper-button-next'
						),
					},
				};

				cardSlider = new Swiper(cardsContainer, settings);
			}
		} else {
			if (cardSlider) {
				cardSlider.destroy();
				cardSlider = null;
			}

			cardsContainer.classList.remove('swiper', 'swiper-backface-hidden');
			cardsContent.classList.remove('swiper-wrapper');
		}
	};

	const cardsObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				cardsObserver.unobserve(entry.target);
				initializeCardSlider();

				if (cards.length > 1) {
					window.addEventListener(
						'resize',
						throttle(() => {
							initializeCardSlider();
						}, 100)
					);
				}
			});
		},
		{ rootMargin: '200px 0px' }
	);

	cardsObserver.observe(cardsContainer);
}
